import type { InstructionStepDto, SupportingMaterialDto } from './types';
import {
    resolveMaterialsForStep,
    stripDuplicateMaterialTitleFromContent,
    stripWorksheetPreamble
} from './instructionStepUtils';
import { getDisplayVideoOptions, isVideoRemovedFromYouTube } from './mediaReviewUtils';
import { VIDEO_REMOVED_FROM_YOUTUBE_MESSAGE } from '../LessonVideoPlayer';
import { renderSupportingMaterialMarkdownToHtml } from './renderSupportingMaterialMarkdown';
import { openLessonPrintWindow } from './printLessonDocument';
import { collectRasterFiguresFromSteps } from './supportingMaterialContentSegments';
import {
    buildPrintHeaderHtml,
    buildPrintSectionHeading,
    escapeHtmlForPrint,
    getFullInstructionPrintLayoutStyles,
    materialCardClass,
    materialTypeBadgeClass
} from './lessonPrintLayout';

function materialTypeLabel(typeKey?: string): string {
    switch (typeKey) {
        case '10':
            return 'Worksheet';
        case '20':
            return 'Reading';
        case '30':
            return 'Video';
        default:
            return 'Material';
    }
}

function getMaterialPrintContent(material: SupportingMaterialDto): string {
    let content = material.e251_Content || '';
    if (material.e251_MaterialType === '10') {
        content = stripWorksheetPreamble(content);
    }
    return stripDuplicateMaterialTitleFromContent(content, material.e251_Title);
}

function buildStepMaterialsListHtml(materials: SupportingMaterialDto[]): string {
    if (!materials.length) return '';
    const items = materials
        .map(m => {
            const typeLabel = materialTypeLabel(m.e251_MaterialType);
            if (m.e251_MaterialType === '30') {
                if (isVideoRemovedFromYouTube(m)) {
                    return `<li><strong>${escapeHtmlForPrint(m.e251_Title || typeLabel)}</strong> (${typeLabel}) — ${escapeHtmlForPrint(VIDEO_REMOVED_FROM_YOUTUBE_MESSAGE)}</li>`;
                }
                const links = getDisplayVideoOptions(m)
                    .map(
                        o =>
                            `<li><a href="${escapeHtmlForPrint(o.e251_VideoUrl || '#')}">${escapeHtmlForPrint(o.e251_VideoTitle || 'Video')}</a></li>`
                    )
                    .join('');
                return `<li><strong>${escapeHtmlForPrint(m.e251_Title || typeLabel)}</strong> (${typeLabel})${
                    links ? `<ul>${links}</ul>` : ''
                }</li>`;
            }
            return `<li><strong>${escapeHtmlForPrint(m.e251_Title || typeLabel)}</strong> (${typeLabel})</li>`;
        })
        .join('');
    return `
      <div class="step-materials">
        <p class="step-materials-label">Materials for this step</p>
        <ul>${items}</ul>
      </div>
    `;
}

function buildMaterialContentHtml(
    material: SupportingMaterialDto,
    pageBreakBefore = false,
    lessonSteps?: InstructionStepDto[]
): string {
    const title = material.e251_Title || 'Supporting material';
    const typeLabel = materialTypeLabel(material.e251_MaterialType);
    const contentHtml = renderSupportingMaterialMarkdownToHtml(
        getMaterialPrintContent(material),
        collectRasterFiguresFromSteps(lessonSteps)
    );
    const breakClass = pageBreakBefore ? ' material-card--break' : '';

    return `
      <article class="${materialCardClass(material.e251_MaterialType)}${breakClass}">
        <header class="material-card-header">
          <span class="${materialTypeBadgeClass(material.e251_MaterialType)}">${escapeHtmlForPrint(typeLabel)}</span>
          <h3 class="material-title">${escapeHtmlForPrint(title)}</h3>
        </header>
        <div class="material-card-body">
          ${contentHtml}
        </div>
      </article>
    `;
}

function buildAppendMaterialsHtml(
    materials: SupportingMaterialDto[],
    pageBreaksBetweenItems: boolean,
    lessonSteps?: InstructionStepDto[]
): string {
    if (!materials.length) return '';
    const items = materials
        .map((material, index) =>
            buildMaterialContentHtml(material, pageBreaksBetweenItems && index > 0, lessonSteps)
        )
        .join('');
    const appendClass = pageBreaksBetweenItems ? 'materials-append materials-append--break' : 'materials-append';
    return `
      <section class="print-section materials-section">
        ${buildPrintSectionHeading('Worksheets & readings')}
        <div class="${appendClass}">${items}</div>
      </section>
    `;
}

function buildStepBlockHtml(
    step: InstructionStepDto,
    supportingMaterials: SupportingMaterialDto[] | undefined,
    lessonSteps: InstructionStepDto[] | undefined,
    sliceWorksheets: boolean
): string {
    const related = resolveMaterialsForStep(step, supportingMaterials, lessonSteps, { sliceWorksheets });
    const stepNumber = step.e251_StepNumber ?? '';
    const summary = step.e251_Summary || `Step ${stepNumber}`;
    const instructions = step.e251_DetailedInstructions?.trim();

    return `
      <article class="step-block">
        <div class="step-block-header">
          ${stepNumber ? `<span class="step-badge">Step ${stepNumber}</span>` : ''}
          <h3 class="step-title">${escapeHtmlForPrint(summary)}</h3>
        </div>
        ${instructions ? `<div class="step-instructions">${escapeHtmlForPrint(instructions)}</div>` : ''}
        ${buildStepMaterialsListHtml(related)}
      </article>
    `;
}

function buildStepsSectionHtml(
    stepsHtml: string
): string {
    if (!stepsHtml) return '';
    return `
      <section class="print-section steps-section">
        ${buildPrintSectionHeading('Step notes')}
        ${stepsHtml}
      </section>
    `;
}

function buildInstructionPrintDocument(title: string, stepsHtml: string, materialsHtml: string): string {
    return `<!DOCTYPE html>
<html>
<head>
  <title>${escapeHtmlForPrint(title)}</title>
  <style>${getFullInstructionPrintLayoutStyles()}</style>
</head>
<body>
  ${buildPrintHeaderHtml(title)}
  ${stepsHtml}
  ${materialsHtml}
</body>
</html>`;
}

export function buildInstructionStepPrintHtml(args: {
    title: string;
    step: InstructionStepDto;
    supportingMaterials?: SupportingMaterialDto[];
    lessonSteps?: InstructionStepDto[];
    appendMaterials?: SupportingMaterialDto[];
    pageBreaksBetweenItems?: boolean;
    printStepNotes?: boolean;
    sliceWorksheets?: boolean;
}): string {
    const pageBreaksBetweenItems = args.pageBreaksBetweenItems ?? false;
    const printStepNotes = args.printStepNotes !== false;
    const sliceWorksheets = args.sliceWorksheets !== false;
    const stepInner = printStepNotes
        ? buildStepBlockHtml(args.step, args.supportingMaterials, args.lessonSteps, sliceWorksheets)
        : '';
    const stepsHtml = buildStepsSectionHtml(stepInner);
    const materialsHtml = buildAppendMaterialsHtml(
        args.appendMaterials ?? [],
        pageBreaksBetweenItems,
        args.lessonSteps
    );

    return buildInstructionPrintDocument(args.title, stepsHtml, materialsHtml);
}

export function buildAllInstructionStepsPrintHtml(args: {
    title: string;
    steps: InstructionStepDto[];
    supportingMaterials?: SupportingMaterialDto[];
    lessonSteps?: InstructionStepDto[];
    appendMaterials?: SupportingMaterialDto[];
    pageBreaksBetweenItems?: boolean;
    printStepNotes?: boolean;
}): string {
    const pageBreaksBetweenItems = args.pageBreaksBetweenItems ?? false;
    const printStepNotes = args.printStepNotes !== false;
    const lessonSteps = args.lessonSteps ?? args.steps;
    const stepInner = printStepNotes
        ? args.steps
              .map(step =>
                  buildStepBlockHtml(step, args.supportingMaterials, lessonSteps, true)
              )
              .join('')
        : '';
    const stepsHtml = buildStepsSectionHtml(stepInner);
    const materialsHtml = buildAppendMaterialsHtml(
        args.appendMaterials ?? [],
        pageBreaksBetweenItems,
        lessonSteps
    );

    return buildInstructionPrintDocument(args.title, stepsHtml, materialsHtml);
}

export function printInstructionStepDocument(html: string, idToken?: string | null): void {
    void openLessonPrintWindow(html, idToken);
}
